import { useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { Box, makeStyles } from '@material-ui/core';
import moment from 'moment';
import _ from 'lodash';
import Dialog from '../../../../../components/Dialog';
import Text from '../../../../../components/Text';
import StyleInput from '../../../../../components/StyleInput';
import { AutoCompleteSelect } from '../../../../../components';
import { Checkbox } from '../../../../DutyAssign/components/Attendance/Setting';
import { Triangle } from '../../Triangle';
import { eadmissionAPI } from '../../../../../apis';
import AutoCompleteMultiCheckbox from '../../../../../components/AutoCompleteMultiCheckbox';
import DatePicker from '../../../../DutyAssign/components/Header/DatePicker';
import AutoCompleteInput from '../../../../../components/AutoCompleteInput';
import { AdmissionContext } from '../../..';
import { GENDER, WT } from '../../../../../comm/constant';

const useStyles = makeStyles((theme) => ({
  border: {
    maxWidth: '1px',
    backgroundColor: '#E4E7EB',
    flex: '1',
  },
  statusIcon: {
    width: '0.625rem',
    height: '0.625rem',
    borderRadius: '50%',
  },
  datePicker: {
    height: '2.5rem',
    width: '22rem',
  },
}));

const AdmissionFilter = ({ open, handleClose, handleUpdate, tabValue, initFilter }) => {
  const { specialtyList } = useContext(AdmissionContext);
  const classes = useStyles();
  const [filter, setFilter] = useState({ ...initFilter });
  const [hospCodeList, setHospCodeList] = useState([]);
  const [errors, setErrors] = useState({
    startDate: '',
    endDate: '',
  });
  const [reset, setReset] = useState(1);

  const checkForm = useCallback(() => {
    if (tabValue !== 'C') {
      return;
    }
    if (moment(filter.startDate).isAfter(filter.endDate, 'days')) {
      // start date不能在end date后面
      setErrors((preErrors) => ({
        ...preErrors,
        startDate: 'StartDate is mandatory.',
        endDate: 'EndDate is mandatory.',
      }));
      return;
    }
    if (filter.aeNo) {
      // aeNo有值的时候，日期不是必填
      setErrors((preErrors) => ({
        ...preErrors,
        startDate: '',
        endDate: '',
      }));
    } else if (moment(filter.startDate).isBefore(moment(filter.endDate).add(-2, 'days'), 'days')) {
      // 时间段不超过3天
      setErrors((preErrors) => ({
        ...preErrors,
        startDate: 'StartDate is mandatory.',
        endDate: 'EndDate is mandatory.',
      }));
    } else {
      // aeNo没值的时候，日期是必填的
      setErrors((preErrors) => ({
        ...preErrors,
        startDate: filter.startDate ? '' : 'StartDate is mandatory.',
        endDate: filter.endDate ? '' : 'EndDate is mandatory.',
      }));
    }
  }, [filter.startDate, filter.endDate, filter.aeNo, tabValue]);

  useEffect(() => {
    checkForm();
  }, [checkForm]);

  const handleUpdateFilter = (type, value) => (e) => {
    switch (type) {
      case 'startDate':
      case 'endDate':
      case 'chiName':
      case 'engName':
      case 'sex':
      case 'specialtys':
      case 'aeNo':
      case 'diagnosis':
      case 'remark':
      case 'handingByIds':
      case 'hospCode':
      case 'waitTime':
        setFilter((preData) => {
          if (type === 'hospCode' && preData.hospCode !== e) {
            setReset((o) => o + 1);
            return { ...preData, handingByIds: [], [type]: e };
          }
          return { ...preData, [type]: e };
        });
        break;
      case 'priority':
        setFilter((preData) => ({
          ...preData,
          priority: _.some(preData.priority, (item) => item === value)
            ? _.filter(preData.priority, (item) => item !== value)
            : [...preData.priority, value],
        }));
        break;
      case 'status':
        setFilter((preData) => ({
          ...preData,
          status: _.some(preData.status, (item) => item === value)
            ? _.filter(preData.status, (item) => item !== value)
            : [...preData.status, value],
        }));
        break;
      default:
        break;
    }
  };

  const handleReset = () => {
    handleUpdate({
      aeNo: '',
      chiName: '',
      diagnosis: '',
      engName: '',
      handingByIds: [],
      priority: [],
      remark: '',
      sex: '',
      status: [],
      hospCode: '',
      startDate: moment().add(-2, 'days'),
      endDate: moment(),
      specialtys: [],
    });
    setReset((o) => o + 1);
  };

  const selectedPriority = useCallback(
    (priority) => {
      return _.some(filter.priority, (item) => item === priority);
    },
    [filter.priority]
  );

  const selectedStatus = useCallback(
    (status) => {
      return _.some(filter.status, (item) => item === status);
    },
    [filter.status]
  );

  const getAdmWard = useCallback(async () => {
    try {
      const { result } = await eadmissionAPI.getAdmWard();
      setHospCodeList(result);
    } catch (error) {}
  }, []);

  useEffect(() => {
    getAdmWard();
  }, [getAdmWard]);

  const itemLists = useMemo(() => {
    let hospList = [];
    let wardList = [];

    hospList = [
      {
        text: '',
        value: '',
      },
      ..._.map(hospCodeList, (item) => ({
        text: item.hospCode,
        value: item.hospId,
      })),
    ];

    wardList = _.map(_.find(hospCodeList, (item) => item.hospId === filter.hospCode)?.wards ?? [], (item) => ({
      text: item.wardName,
      value: item.wardId,
    }));

    return { specialtyList, hospList, wardList };
  }, [hospCodeList, filter.hospCode]);

  const handleSubmit = useCallback(() => {
    if (_.every(errors, _.isEmpty)) {
      handleUpdate(filter);
    }
  }, [filter, errors]);

  return (
    <Dialog
      open={open}
      handleClose={handleClose}
      handleConfirm={handleSubmit}
      handleCancel={handleReset}
      title="Filter"
      cancel="Reset"
      confirm="Confirm"
      width="81.25rem"
      dialogfootStyle={{ boxShadow: '0px -1px 0px 0px #E4E7EB', paddingTop: '1rem' }}
      dialogRootStyle={{ paddingBottom: '1rem' }}
    >
      <Box display="flex" width="100%" gridGap="2rem" justifyContent="space-between">
        {/* 左边 */}
        <Box display="flex" flexDirection="column" gridGap="1.87rem">
          {tabValue === 'C' && (
            <Box display="flex" flexDirection="column" gridGap="0.81rem" width="100%">
              <Text color="#333" fontSize="0.875rem">
                Date:
              </Text>
              <Box display="flex">
                <DatePicker
                  className={classes.datePicker}
                  m="0"
                  selectDate={filter.startDate}
                  handleSelectDate={handleUpdateFilter('startDate')}
                  allowNull={true}
                  placeholder="StartDate"
                  invalidDateMessage={errors.startDate}
                  error={Boolean(errors.startDate)}
                />
                <Box display="flex" alignItems="center" justifyContent="center" width="1.5rem">
                  -
                </Box>
                <DatePicker
                  className={classes.datePicker}
                  m="0"
                  selectDate={filter.endDate}
                  handleSelectDate={handleUpdateFilter('endDate')}
                  allowNull={true}
                  placeholder="EndDate"
                  invalidDateMessage={errors.endDate}
                  error={Boolean(errors.endDate)}
                />
              </Box>
            </Box>
          )}
          <Box display="flex" gridGap="1.5rem">
            <Box display="flex" flexDirection="column" gridGap="0.81rem" width="22rem">
              <Text color="#333" fontSize="0.875rem">
                Patient Chi Name
              </Text>
              <StyleInput value={filter.chiName} onChange={handleUpdateFilter('chiName')} />
            </Box>
            <Box display="flex" flexDirection="column" gridGap="0.81rem" width="22rem">
              <Text color="#333" fontSize="0.875rem">
                Patient Eng Name
              </Text>
              <StyleInput value={filter.engName} onChange={handleUpdateFilter('engName')} />
            </Box>
          </Box>
          <Box display="flex" gridGap="1.5rem">
            <Box display="flex" flexDirection="column" gridGap="0.81rem" width="100%">
              <Text color="#333" fontSize="0.875rem">
                Specialty
              </Text>
              <AutoCompleteMultiCheckbox
                itemList={specialtyList}
                value={filter.specialtys}
                onChange={handleUpdateFilter('specialtys')}
                height="3.5rem"
                placeholder="Specialty"
                freeSolo
                // highlight="#FFFFCC"
                selectAll={false}
              />
            </Box>
          </Box>
          <Box display="flex" gridGap="1.5rem">
            <Box display="flex" flexDirection="column" gridGap="0.81rem" width="22rem">
              <Text color="#333" fontSize="0.875rem">
                Gender
              </Text>
              <AutoCompleteSelect
                placeholder="Select"
                itemList={GENDER}
                value={filter.sex}
                onChange={handleUpdateFilter('sex')}
              />
            </Box>
            <Box display="flex" flexDirection="column" gridGap="0.81rem" width="22rem">
              <Text color="#333" fontSize="0.875rem">
                AE No.
              </Text>
              <StyleInput value={filter.aeNo} onChange={handleUpdateFilter('aeNo')} />
            </Box>
          </Box>
          <Box display="flex" gridGap="1.5rem">
            <Box display="flex" flexDirection="column" gridGap="0.81rem" width="22rem">
              <Text color="#333" fontSize="0.875rem">
                Diagnosis
              </Text>
              <StyleInput value={filter.diagnosis} onChange={handleUpdateFilter('diagnosis')} />
            </Box>
            <Box display="flex" flexDirection="column" gridGap="0.81rem" width="22rem">
              <Text color="#333" fontSize="0.875rem">
                Remarks
              </Text>
              <StyleInput value={filter.remark} onChange={handleUpdateFilter('remark')} />
            </Box>
          </Box>
        </Box>
        <div className={classes.border} />
        {/* 右边 */}
        <Box display="flex" flexDirection="column" gridGap="2.7rem" width="28rem">
          <Box display="flex" flexDirection="column" gridGap="0.81rem" width="100%">
            <Text color="#333" fontSize="0.875rem">
              W.T.
            </Text>
            <AutoCompleteSelect
              placeholder="Select"
              itemList={WT}
              value={filter.waitTime}
              onChange={handleUpdateFilter('waitTime')}
            />
          </Box>
          <Box display="flex" flexDirection="column" gridGap="1.62rem">
            <Text color="#333" fontSize="1rem">
              Priority
            </Text>
            <Box display="flex" gridGap="1.75rem">
              <Box display="flex" gridGap="0.75rem">
                <Checkbox check={selectedPriority('1')} handleChange={handleUpdateFilter('priority', '1')} />
                <Triangle number={1} color="#951B07" />
              </Box>
              <Box display="flex" gridGap="0.75rem">
                <Checkbox check={selectedPriority('0')} handleChange={handleUpdateFilter('priority', '0')} />
                <Box
                  width="1.56rem"
                  height="1.3rem"
                  borderRadius="2px"
                  bgcolor="#C0C6CB"
                  color="#FFF"
                  textAlign="center"
                  p="5% 0"
                  fontSize="0.75rem"
                >
                  NA
                </Box>
              </Box>
            </Box>
          </Box>
          <Box display="flex" flexDirection="column" gridGap="1.62rem">
            <Text color="#333" fontSize="1rem">
              Current Status
            </Text>
            <Box display="grid" gridTemplateColumns="1fr 1fr" gridGap="2.25rem">
              {statusMapping(tabValue === 'C').map((item) => (
                <Box display="flex" gridGap="0.69rem" key={item.key}>
                  <Checkbox check={selectedStatus(item.key)} handleChange={handleUpdateFilter('status', item.key)} />
                  <Box display="flex" gridGap="0.38rem" alignItems="center">
                    <div className={classes.statusIcon} style={{ backgroundColor: item.bgcolor }} />
                    <Text>{item.text}</Text>
                  </Box>
                </Box>
              ))}
            </Box>
          </Box>
          <Box display="flex" flexDirection="column" gridGap="1rem">
            <Text color="#333" fontSize="1rem">
              Waiting For
            </Text>
            <AutoCompleteSelect
              placeholder="Hospital"
              itemList={itemLists.hospList}
              value={filter.hospCode}
              onChange={handleUpdateFilter('hospCode')}
            />
            <AutoCompleteMultiCheckbox
              reset={reset}
              selectAll={false}
              placeholder="Unit"
              itemList={itemLists.wardList}
              value={filter.handingByIds}
              onChange={handleUpdateFilter('handingByIds')}
            />
          </Box>
        </Box>
      </Box>
    </Dialog>
  );
};

const statusMapping = (completed) =>
  completed
    ? [
        {
          key: 'C',
          text: 'Completed',
          bgcolor: '#34C759',
        },

        {
          key: 'W',
          text: 'Withdrawn',
          bgcolor: '#767576',
        },
      ]
    : [
        {
          key: 'PD',
          text: 'Pending',
          bgcolor: '#FA8A82',
        },
        {
          key: 'IP',
          text: 'In Progress',
          bgcolor: '#FECA06',
        },
        // {
        //   key: 'H',
        //   text: 'Hold',
        //   bgcolor: '#A6B0C3',
        // },
        // {
        //   key: 'W',
        //   text: 'Withdrawn',
        //   bgcolor: '#767576',
        // },
        // {
        //   key: 'C',
        //   text: 'Completed',
        //   bgcolor: '#34C759',
        // },
      ];

export default AdmissionFilter;
